"use client";

import { useEffect } from "react";
import { isChunkLoadError, reloadOnceForChunkError } from "@/lib/chunkReload";

// Segment error boundary for all app routes (there was none before — errors fell through to
// Next's bare "This page couldn't load" screen). A ChunkLoadError caught here means the running
// build is stale after a deploy, so we reload once to fetch the current chunks rather than
// stranding the user (this was the wallet-dashboard symptom). Any other error shows a simple
// retry UI.
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const chunk = isChunkLoadError(error);

  useEffect(() => {
    if (chunk) reloadOnceForChunkError();
  }, [chunk]);

  if (chunk) {
    // A guarded reload is in flight (or was just attempted) — keep the screen quiet.
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 p-6 text-center">
        <p className="text-[#666] font-roboto text-sm m-0">Updating to the latest version…</p>
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 p-6 text-center">
      <h1 className="text-[#1A1A1A] font-roboto text-lg font-semibold m-0">Something went wrong</h1>
      <p className="text-[#666] font-roboto text-sm m-0">Please try again.</p>
      <div className="flex gap-3">
        <button
          onClick={() => reset()}
          className="px-4 py-2 rounded-[8px] bg-[#195AFF] text-white font-roboto text-sm"
        >
          Try again
        </button>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 rounded-[8px] border border-[#D9D9D9] text-[#666] font-roboto text-sm"
        >
          Reload
        </button>
      </div>
    </div>
  );
}
